import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useShopSettingFlags } from '@/hooks/queries/useShopSettingFlags';

export const TOUR_STORAGE_KEY = 'product_tour_done';
const TOUR_SETTING_KEY = 'product_tour_done';
/** Delay before the tour opens, so the dashboard KPIs render first. */
const OPEN_DELAY_MS = 800;

interface ProductTourState {
  open: boolean;
  /** Set when the user asks to replay the tour (Guide page). */
  restartRequested: boolean;
  setOpen: (open: boolean) => void;
  requestRestart: () => void;
  clearRestart: () => void;
}

export const useProductTourStore = create<ProductTourState>((set) => ({
  open: false,
  restartRequested: false,
  setOpen: (open) => set({ open }),
  requestRestart: () => set({ restartRequested: true }),
  clearRestart: () => set({ restartRequested: false }),
}));

function readLocalDone(): boolean {
  try {
    return localStorage.getItem(TOUR_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeLocalDone(): void {
  try {
    localStorage.setItem(TOUR_STORAGE_KEY, '1');
  } catch {
    // Ignore
  }
}

/**
 * Drives the first-run product tour: opens it once on the dashboard for
 * fresh accounts, persists completion in the shop settings key/value store
 * (shared across devices) with a localStorage fallback.
 */
export function useProductTour() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const open = useProductTourStore((s) => s.open);
  const setOpen = useProductTourStore((s) => s.setOpen);
  const restartRequested = useProductTourStore((s) => s.restartRequested);
  const clearRestart = useProductTourStore((s) => s.clearRestart);

  const onDashboard = pathname.includes('/dashboard');
  const eligible = Boolean(user) && !user?.is_admin && !user?.needs_onboarding && onDashboard;
  const localDone = readLocalDone();

  const { data: flags } = useShopSettingFlags(eligible && !localDone && !restartRequested);
  const serverDone = flags === undefined ? undefined : flags[TOUR_SETTING_KEY] === '1';

  // First-run auto-open once we know the tour was never completed.
  useEffect(() => {
    if (!eligible || open || restartRequested) return;
    if (localDone || serverDone !== false) return;
    const timer = setTimeout(() => setOpen(true), OPEN_DELAY_MS);
    return () => clearTimeout(timer);
  }, [eligible, open, restartRequested, localDone, serverDone, setOpen]);

  // Replay requested from the Guide page — opens as soon as we land on the
  // dashboard, regardless of the completion flags.
  useEffect(() => {
    if (!restartRequested || !onDashboard || !user) return;
    const timer = setTimeout(() => {
      clearRestart();
      setOpen(true);
    }, OPEN_DELAY_MS);
    return () => clearTimeout(timer);
  }, [restartRequested, onDashboard, user, clearRestart, setOpen]);

  const markDone = useCallback(() => {
    setOpen(false);
    writeLocalDone();
    queryClient.setQueryData(
      ['settings', 'ux-flags'],
      (prev: Record<string, string> | undefined) => ({ ...prev, [TOUR_SETTING_KEY]: '1' }),
    );
    api.post('/settings', { key: TOUR_SETTING_KEY, value: '1' }).catch(() => {
      // Best-effort — the localStorage flag keeps the tour dismissed here.
    });
  }, [queryClient, setOpen]);

  return { open, markDone };
}
